/**
 * S-09 — Platform (ControlPlane :5200) wire schemas, Zod-parsed at the seam.
 *
 * Mirrors TenantDto field-by-field (ctrl.tenants, camelCase inside the [STD-API-2]
 * envelope). Statuses mirror the ctrl DDL CHECK constraint — an unknown status is
 * contract drift and must fail loudly, exactly like the education schemas.
 * NOTE: ids use z.guid() (backend Guid), see the 2026-07-06 decision in education.ts.
 */
import { z } from 'zod';

import { pagedListSchema } from './education';

/**
 * ctrl.tenants.status CHECK — the 7 B-3 lifecycle states (005_tenant_lifecycle_states.sql),
 * mirroring backend `TenantStatus.All`. An unknown status is contract drift and must fail loudly.
 */
export const tenantStatuses = [
  'provisioning',
  'active',
  'trial_expired',
  'soft_suspended',
  'hard_suspended',
  'termination_requested',
  'terminated'
] as const;

/** ISO-8601 timestamp as serialized by System.Text.Json */
const isoDate = z.string().min(1);

/** TenantDto — B-3 added `graceUntil` (set while status = termination_requested; null otherwise). */
export const tenantSchema = z.object({
  id: z.guid(),
  slug: z.string(),
  name: z.string(),
  dbName: z.string().nullish(),
  status: z.enum(tenantStatuses),
  schemaVersion: z.string().nullish(),
  region: z.string(),
  notes: z.string().nullish(),
  createdAt: isoDate,
  updatedAt: isoDate,
  graceUntil: isoDate.nullish(),
  // SA-P2: the plan of the EFFECTIVE licence; filled by the list and get reads only (null on write responses
  // and for a tenant with no effective licence).
  planCode: z.string().nullish(),
  planName: z.string().nullish(),
  // SA-P3: the effective licence's status and dates — lets the list flag a tenant in grace. A plain string,
  // not the enum: a display field, and a new server status must not blank the tenant list.
  licenseStatus: z.string().nullish(),
  licenseValidUntil: isoDate.nullish(),
  licenseGraceUntil: isoDate.nullish()
});

export const tenantListSchema = pagedListSchema(tenantSchema);

export type Tenant = z.infer<typeof tenantSchema>;
export type TenantStatus = (typeof tenantStatuses)[number];

/**
 * B-3 lifecycle transition map — mirrors backend `TenantLifecycle.Allowed` so the UI only offers
 * transitions the ControlPlane will accept (the backend remains the authority; this just prevents
 * dead buttons). Each target maps to the write endpoint that drives it.
 */
export const tenantTransitions: Readonly<Record<TenantStatus, readonly TenantStatus[]>> = {
  provisioning: ['active', 'termination_requested'],
  active: ['trial_expired', 'soft_suspended', 'hard_suspended', 'termination_requested'],
  trial_expired: ['active', 'soft_suspended', 'hard_suspended', 'termination_requested'],
  soft_suspended: ['active', 'hard_suspended', 'termination_requested'],
  hard_suspended: ['active', 'soft_suspended', 'termination_requested'],
  termination_requested: ['active', 'terminated'],
  terminated: []
};

/** The lifecycle write endpoints (POST /api/v1/tenants/{id}/{action}); `terminated` is worker-driven, not an API verb. */
export const tenantLifecycleActions = ['activate', 'suspend', 'hard-suspend', 'expire-trial', 'terminate'] as const;
export type TenantLifecycleAction = (typeof tenantLifecycleActions)[number];

/** Target state → the write endpoint action that reaches it (drives which button an operator sees). */
export const tenantActionForTarget: Readonly<Record<TenantStatus, TenantLifecycleAction | null>> = {
  active: 'activate',
  soft_suspended: 'suspend',
  hard_suspended: 'hard-suspend',
  trial_expired: 'expire-trial',
  termination_requested: 'terminate',
  provisioning: null,
  terminated: null
};

/** CreateTenantCommand body [STD-API-2]. */
export interface CreateTenantRequest {
  slug: string;
  name: string;
  region?: string | null;
  notes?: string | null;
}

/** UpdateTenantCommand body (name/region/notes; slug + status are not editable here). */
export interface UpdateTenantRequest {
  name: string;
  region: string;
  notes?: string | null;
}

/** Suspend / hard-suspend optional reason. */
export interface SuspendTenantRequest {
  reason?: string | null;
}

// ---- B-Core credentials vault (secrets.tenant_secrets, spec §5.3.2) ----
/** ck_tenant_secrets_type (002_secrets_vault.sql) — 10 credential types, backend CredentialTypes.All. */
export const credentialTypes = [
  'stripe_secret_key', 'stripe_webhook_secret', 'smtp_password', 'twilio_auth_token', 'whatsapp_api_key',
  'zoom_client_secret', 'openai_api_key', 'fcm_service_account', 's3_secret_key', 'generic_api_key'
] as const;
export type CredentialType = (typeof credentialTypes)[number];

/** TenantSecretDto — vault row metadata; NEVER carries the value (only reveal does). */
export const tenantSecretSchema = z.object({
  tenantId: z.guid(),
  credentialType: z.string(),
  name: z.string().nullish(),
  createdAtUtc: isoDate,
  updatedAtUtc: isoDate,
  rotatedAtUtc: isoDate.nullish()
});
export const tenantSecretListSchema = z.array(tenantSecretSchema);

/** RevealedSecretDto — plaintext, only from the reveal endpoint (separate permission). */
export const revealedSecretSchema = z.object({ credentialType: z.string(), value: z.string() });

/** DeleteSecretResult — soft-delete outcome. */
export const deleteSecretResultSchema = z.object({ credentialType: z.string(), deleted: z.boolean() });

/** SetTenantSecretCommand body (tenantId + type travel in the URL). */
export interface SetSecretRequest {
  name?: string | null;
  value: string;
}

export type TenantSecret = z.infer<typeof tenantSecretSchema>;
export type RevealedSecret = z.infer<typeof revealedSecretSchema>;

// ---- B-Core custom domains (ctrl.tenant_domains) ----
/** ck_tenant_domains_state (001_init_control_plane.sql). */
export const domainVerificationStates = ['pending', 'verified', 'failed'] as const;

/** TenantDomainDto — verification_token present so the operator can create the DNS TXT record. */
export const tenantDomainSchema = z.object({
  id: z.guid(),
  tenantId: z.guid(),
  domain: z.string(),
  kind: z.string(),
  isPrimary: z.boolean(),
  verificationState: z.enum(domainVerificationStates),
  verificationToken: z.string().nullish(),
  verifiedAtUtc: isoDate.nullish(),
  createdAtUtc: isoDate
});
export const tenantDomainListSchema = z.array(tenantDomainSchema);

/** AddCustomDomainCommand body (tenantId travels in the URL). */
export interface AddCustomDomainRequest {
  domain: string;
}

export type TenantDomain = z.infer<typeof tenantDomainSchema>;
export type DomainVerificationState = (typeof domainVerificationStates)[number];

// ---- Tenant quotas (P5-CP+) ----
// Effective limit resolution: override > plan > default > unlimited (ctrl DDL authority).
export const quotaSources = ['override', 'plan', 'default', 'unlimited'] as const;

/** TenantQuotaDto — one resolved quota line (limit + where it came from + current usage). */
export const tenantQuotaSchema = z.object({
  code: z.string(),
  name: z.string(),
  unit: z.string(),
  resetPeriod: z.string(),
  effectiveLimit: z.number().nullish(), // null = unlimited
  source: z.enum(quotaSources),
  used: z.number(),
  overrideExpiresAt: isoDate.nullish()
});

/** TenantQuotasDto — resolved quota report the Quota Guard reasons about. */
export const tenantQuotasSchema = z.object({
  tenantId: z.guid(),
  quotas: z.array(tenantQuotaSchema)
});

export type TenantQuota = z.infer<typeof tenantQuotaSchema>;
export type TenantQuotas = z.infer<typeof tenantQuotasSchema>;
export type QuotaSource = (typeof quotaSources)[number];

// ---- Usage-snapshot history (C-2 Slice 3a — trend feed from ctrl.tenant_usage_snapshots) ----
/** UsageSnapshotPointDto — one quota's usage on a UTC day (snapshotDate is a DateOnly, "2026-07-11"). */
export const usageSnapshotPointSchema = z.object({
  quotaCode: z.string(),
  quotaName: z.string(),
  unit: z.string(),
  snapshotDate: z.string().min(1),
  used: z.number(),
  effectiveLimit: z.number().nullish(), // null = unlimited
  limitSource: z.string()
});

/** TenantUsageHistoryDto — the tenant's snapshot time-series over [fromDate, toDate], oldest first. */
export const tenantUsageHistorySchema = z.object({
  tenantId: z.guid(),
  fromDate: z.string().min(1),
  toDate: z.string().min(1),
  points: z.array(usageSnapshotPointSchema)
});

export type UsageSnapshotPoint = z.infer<typeof usageSnapshotPointSchema>;
export type TenantUsageHistory = z.infer<typeof tenantUsageHistorySchema>;

// ---- Migration Orchestrator status (C-2 Slice 3c — read-only fleet schema drift) ----
/** One tenant's migration standing (status kept as a plain string — a display field, not gated). */
export const tenantMigrationSchema = z.object({
  id: z.guid(),
  slug: z.string(),
  status: z.string(),
  schemaVersion: z.string().nullish(),
  upToDate: z.boolean()
});

/** MigrationStatusDto — fleet target (config or fleet-max) + per-tenant drift. */
export const migrationStatusSchema = z.object({
  targetVersion: z.string().nullish(),
  targetSource: z.string(),
  tenantsTotal: z.number().int(),
  tenantsUpToDate: z.number().int(),
  tenantsBehind: z.number().int(),
  tenants: z.array(tenantMigrationSchema)
});

export type TenantMigration = z.infer<typeof tenantMigrationSchema>;
export type MigrationStatus = z.infer<typeof migrationStatusSchema>;

// ---- Entitlements (C-8 — current tenant's licensed modules for FE feature-flag gating) ----
/** EntitlementsDto — flattened module codes (core + effective plan); "*" = all modules. */
export const entitlementsSchema = z.object({
  tenantId: z.string(),
  planCode: z.string().nullish(),
  licenseStatus: z.string().nullish(),
  modules: z.array(z.string())
});
export type Entitlements = z.infer<typeof entitlementsSchema>;

// ---- Plans + tenant license (P5-CP+) ----
/** ctrl.licenses.status — Effective = trial|active|grace; only trial|active are API-assignable. */
export const licenseStatuses = ['trial', 'active', 'grace', 'expired', 'cancelled'] as const;
export const assignableLicenseStatuses = ['trial', 'active'] as const;

/** PlanDto */
export const planSchema = z.object({
  id: z.guid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  isPublic: z.boolean(),
  sortOrder: z.number().int(),
  moduleCodes: z.array(z.string()),
  createdAt: isoDate,
  updatedAt: isoDate
});
export const planListSchema = pagedListSchema(planSchema);

/**
 * ModuleDto — an entitlement building block; isCore modules are always granted. Named
 * `entitlementModule*` to avoid colliding with education.ts's course `moduleSchema`.
 */
export const entitlementModuleSchema = z.object({
  id: z.guid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  isCore: z.boolean(),
  /** SA-P2: how many LIVE plans include this module — 0 is what allows retiring it. Defaulted for older servers. */
  planCount: z.number().int().default(0)
});
export const entitlementModuleListSchema = pagedListSchema(entitlementModuleSchema);
export type EntitlementModule = z.infer<typeof entitlementModuleSchema>;

/** CreatePlanCommand body. */
export interface CreatePlanRequest {
  code: string;
  name: string;
  description?: string | null;
  isPublic?: boolean;
  sortOrder?: number;
}

/** UpdatePlanCommand body (code is immutable; not editable here). */
export interface UpdatePlanRequest {
  name: string;
  description?: string | null;
  isPublic: boolean;
  sortOrder: number;
}

/** LicenseDto — a tenant's license (effective or historical). */
export const licenseSchema = z.object({
  id: z.guid(),
  tenantId: z.guid(),
  planId: z.guid(),
  planCode: z.string(),
  status: z.enum(licenseStatuses),
  validFrom: isoDate,
  validUntil: isoDate.nullish(),
  seats: z.number().int().nullish(),
  autoRenew: z.boolean(),
  billingRef: z.string().nullish(),
  createdAt: isoDate,
  // SA-P3: when a licence in grace stops entitling its tenant (then: expired + tenant hard-suspended).
  graceUntil: isoDate.nullish(),
  // SA-P3: filled by the history read only.
  planName: z.string().nullish()
});
export const licenseListSchema = z.array(licenseSchema);

/** PlatformStatsDto — platform-wide overview counters (P5-PW); per-status counts follow the B-3 states. */
export const platformStatsSchema = z.object({
  totalTenants: z.number().int(),
  provisioning: z.number().int(),
  active: z.number().int(),
  trialExpired: z.number().int(),
  softSuspended: z.number().int(),
  hardSuspended: z.number().int(),
  terminationRequested: z.number().int(),
  terminated: z.number().int(),
  plans: z.number().int(),
  modules: z.number().int(),
  activeLicenses: z.number().int()
});

export type Plan = z.infer<typeof planSchema>;
export type License = z.infer<typeof licenseSchema>;
export type PlatformStats = z.infer<typeof platformStatsSchema>;
export type LicenseStatusName = (typeof licenseStatuses)[number];
export type AssignableLicenseStatus = (typeof assignableLicenseStatuses)[number];

/** PlatformSettingsDto — C-14b super-admin global settings (singleton). */
export const platformSettingsSchema = z.object({
  defaultLocale: z.string(),
  enabledLocales: z.array(z.string()),
  opsAlertWebhook: z.string().nullish(),
  sagaAlertEnabled: z.boolean()
});
export type PlatformSettings = z.infer<typeof platformSettingsSchema>;

// ---------------------------------------------------------------------------
// CLOUD-STOR — per-tenant cloud file storage (Integrations tab 4).
// ---------------------------------------------------------------------------

/**
 * Binding lifecycle, mirroring StorageBindingStatus on the server.
 *
 * ⚠ ORDER IS THE HAPPY PATH and the UI leans on it: pending → testing → migrating → active, with
 * `failed` off to the side. `failed` means the tenant is back on SERVER-LOCAL storage and still
 * working — it is not an outage, so it must never be rendered as one.
 */
export const storageBindingStatuses = ['pending', 'testing', 'migrating', 'active', 'failed'] as const;

/** Storable providers. There is deliberately no 'local': server-local is the ABSENCE of a binding. */
export const storageProviders = ['nextcloud', 's3'] as const;

/**
 * StorageBindingStatusDto.
 *
 * ⚠ CARRIES NO SECRET, and must never grow one. `hasSecret` + `secretLast4` are the whole contract:
 * four characters let an admin tell WHICH credential is stored and cannot reconstruct it.
 */
export const storageBindingSchema = z.object({
  tenantId: z.guid(),
  provider: z.enum(storageProviders),
  status: z.enum(storageBindingStatuses),
  baseUrl: z.string(),
  username: z.string().nullish(),
  hasSecret: z.boolean(),
  secretLast4: z.string().nullish(),
  bucket: z.string().nullish(),
  region: z.string().nullish(),
  rootPath: z.string(),
  // Migration progress. NULL means "not measured yet", which is NOT the same as zero — the UI shows
  // "preparing" rather than "0 of 0".
  sourceObjects: z.number().int().nullish(),
  sourceBytes: z.number().int().nullish(),
  migratedObjects: z.number().int(),
  migratedBytes: z.number().int(),
  verifiedObjects: z.number().int().nullish(),
  verifiedBytes: z.number().int().nullish(),
  migrationStartedAtUtc: isoDate.nullish(),
  activatedAtUtc: isoDate.nullish(),
  localPurgeAfterUtc: isoDate.nullish(),
  lastSuccessAtUtc: isoDate.nullish(),
  lastErrorAtUtc: isoDate.nullish(),
  lastError: z.string().nullish(),
  migrationPercent: z.number().int().nullish()
});

/**
 * StorageBindingProbeDto — the connection test's answer.
 *
 * ⚠ A FAILED PROBE IS A 200 WITH `ok: false`, not an HTTP error: the message is the answer the admin
 * pressed the button for. Same contract as the SMTP test.
 */
export const storageBindingProbeSchema = z.object({
  ok: z.boolean(),
  failedStep: z.string().nullish(),
  elapsedMs: z.number().int(),
  quotaBytes: z.number().int().nullish(),
  error: z.string().nullish(),
  hint: z.string().nullish()
});

export type StorageBinding = z.infer<typeof storageBindingSchema>;
export type StorageBindingProbe = z.infer<typeof storageBindingProbeSchema>;
export type StorageBindingStatusName = (typeof storageBindingStatuses)[number];
export type StorageProviderName = (typeof storageProviders)[number];

/**
 * PUT body. `secret` is OPTIONAL and blank MEANS KEEP — the form never prefills one, so sending an
 * empty string would otherwise wipe the stored credential on any unrelated edit (a live-tested bug in
 * the payment tab).
 */
export interface StorageBindingRequest {
  provider: StorageProviderName;
  baseUrl: string;
  username?: string | null;
  secret?: string | null;
  bucket?: string | null;
  region?: string | null;
  usePathStyle: boolean;
  rootPath: string;
}

/**
 * True while the tenant's files are being copied or already live in their own cloud — the states in
 * which the server refuses edits and deletion (409), because changing the target then would strand
 * data.
 */
export function isStorageBindingLocked(status: StorageBindingStatusName): boolean {
  return status === 'migrating' || status === 'active';
}

// ---- SA-P2: quota catalogue (ctrl.quota_definitions + ctrl.plan_quotas) ----

/** reset_period CHECK (ck_quota_defs_reset). */
export const quotaResetPeriods = ['none', 'daily', 'monthly'] as const;
export type QuotaResetPeriod = (typeof quotaResetPeriods)[number];

/** QuotaDefinitionDto — one row of the quota catalogue. defaultLimit null = unlimited unless a plan says otherwise. */
export const quotaDefinitionSchema = z.object({
  id: z.guid(),
  code: z.string(),
  name: z.string(),
  unit: z.string(),
  resetPeriod: z.string(),
  defaultLimit: z.number().nullish(),
  planCount: z.number().int().default(0)
});
export const quotaDefinitionListSchema = z.array(quotaDefinitionSchema);
export type QuotaDefinition = z.infer<typeof quotaDefinitionSchema>;

/** One definition as a plan sees it; planLimit null = the plan inherits the default. */
export const planQuotaLineSchema = z.object({
  code: z.string(),
  name: z.string(),
  unit: z.string(),
  resetPeriod: z.string(),
  defaultLimit: z.number().nullish(),
  planLimit: z.number().nullish()
});
export const planQuotasSchema = z.object({
  planId: z.guid(),
  planCode: z.string(),
  quotas: z.array(planQuotaLineSchema)
});
export type PlanQuotaLine = z.infer<typeof planQuotaLineSchema>;
export type PlanQuotas = z.infer<typeof planQuotasSchema>;

export const removedQuotaOverrideSchema = z.object({ tenantId: z.guid(), quotaCode: z.string(), removed: z.boolean() });

// ---- SA-P3: licence lifecycle + audit log ----

/** A licence the platform admin should look at: in grace, or ending within the window. */
export const licenseAttentionSchema = z.object({
  licenseId: z.guid(),
  tenantId: z.guid(),
  tenantSlug: z.string(),
  tenantName: z.string(),
  tenantStatus: z.string(),
  planCode: z.string(),
  licenseStatus: z.string(),
  validUntil: isoDate.nullish(),
  graceUntil: isoDate.nullish(),
  autoRenew: z.boolean()
});
export const licenseAttentionListSchema = z.array(licenseAttentionSchema);
export type LicenseAttention = z.infer<typeof licenseAttentionSchema>;

export const licenseLifecycleResultSchema = z.object({
  examined: z.number().int(),
  trialsExpired: z.number().int(),
  gracesStarted: z.number().int(),
  expired: z.number().int(),
  tenantsBlocked: z.number().int(),
  failed: z.number().int()
});
export type LicenseLifecycleResult = z.infer<typeof licenseLifecycleResultSchema>;

/**
 * One control-plane audit row. `changes` is the sanitised JSON payload as a STRING (long values are replaced
 * by a length marker server-side); `subjectTenantId` is the tenant the row is about, when the payload names one.
 */
export const auditEntrySchema = z.object({
  id: z.guid(),
  occurredAt: isoDate,
  actorId: z.guid().nullish(),
  actorType: z.string(),
  action: z.string(),
  type: z.string(),
  subjectTenantId: z.guid().nullish(),
  correlationId: z.guid().nullish(),
  changes: z.string().nullish()
});
export const auditEntryListSchema = pagedListSchema(auditEntrySchema);
export type AuditEntry = z.infer<typeof auditEntrySchema>;

// ---- SA-P4: platform billing (Stripe invoices the tenant; GBP; amounts in pence) ----

/** The two recurring intervals a plan or add-on can be priced for. */
export const billingIntervals = ['month', 'year'] as const;
export type BillingIntervalName = (typeof billingIntervals)[number];

/**
 * One interval's price for a plan. `synced` is false while the Stripe prices are missing — a plan without a
 * synced line for an interval cannot be sold on it (the create call answers 409).
 */
export const priceLineSchema = z.object({
  interval: z.string(),
  baseAmountMinor: z.number().int(),
  perSeatAmountMinor: z.number().int(),
  synced: z.boolean()
});
export const planPricingSchema = z.object({
  planId: z.guid(),
  planCode: z.string(),
  currency: z.string(),
  prices: z.array(priceLineSchema)
});
export type PriceLine = z.infer<typeof priceLineSchema>;
export type PlanPricing = z.infer<typeof planPricingSchema>;

/** A flat per-interval add-on price for an optional (non-core) module. */
export const modulePriceSchema = z.object({
  moduleId: z.guid(),
  moduleCode: z.string(),
  moduleName: z.string(),
  interval: z.string(),
  amountMinor: z.number().int(),
  synced: z.boolean()
});
export const modulePriceListSchema = z.array(modulePriceSchema);
export type ModulePrice = z.infer<typeof modulePriceSchema>;

export const subscriptionAddonSchema = z.object({
  moduleCode: z.string(),
  moduleName: z.string(),
  amountMinor: z.number().int()
});

/**
 * The tenant's live subscription. Amounts are the ones COPIED when it was created or changed — a later
 * catalogue price change does not move them. `status` is Stripe's own vocabulary and stays a plain string:
 * Stripe adds values, and an enum would reject the whole response on the first one it does not know.
 */
export const subscriptionSchema = z.object({
  id: z.guid(),
  planCode: z.string(),
  planName: z.string(),
  interval: z.string(),
  seats: z.number().int(),
  currency: z.string(),
  baseAmountMinor: z.number().int(),
  perSeatAmountMinor: z.number().int(),
  addonsAmountMinor: z.number().int(),
  intervalAmountMinor: z.number().int(),
  status: z.string(),
  currentPeriodEnd: isoDate.nullish(),
  cancelAtPeriodEnd: z.boolean(),
  addons: z.array(subscriptionAddonSchema),
  createdAt: isoDate
});
export type Subscription = z.infer<typeof subscriptionSchema>;

/** `configured` false = no Stripe key in this environment; reads still work, writes answer 503. */
export const tenantBillingSchema = z.object({
  tenantId: z.guid(),
  configured: z.boolean(),
  billingEmail: z.string().nullish(),
  subscription: subscriptionSchema.nullish()
});
export type TenantBilling = z.infer<typeof tenantBillingSchema>;

export const platformInvoiceSchema = z.object({
  id: z.string(),
  number: z.string().nullish(),
  status: z.string(),
  amountDueMinor: z.number().int(),
  amountPaidMinor: z.number().int(),
  currency: z.string(),
  createdAt: isoDate,
  dueDate: isoDate.nullish(),
  hostedInvoiceUrl: z.string().nullish()
});
export const platformInvoiceListSchema = z.array(platformInvoiceSchema);
export type PlatformInvoice = z.infer<typeof platformInvoiceSchema>;

export const renewalSchema = z.object({
  tenantId: z.guid(),
  tenantName: z.string(),
  planCode: z.string(),
  interval: z.string(),
  intervalAmountMinor: z.number().int(),
  status: z.string(),
  currentPeriodEnd: isoDate.nullish(),
  cancelAtPeriodEnd: z.boolean()
});
export const billingOverviewSchema = z.object({
  configured: z.boolean(),
  currency: z.string(),
  monthlyRecurringRevenueMinor: z.number().int(),
  liveSubscriptions: z.number().int(),
  pastDueSubscriptions: z.number().int(),
  upcomingRenewals: z.array(renewalSchema)
});
export type Renewal = z.infer<typeof renewalSchema>;
export type BillingOverview = z.infer<typeof billingOverviewSchema>;

export interface SubscriptionSelection {
  planCode: string;
  interval: BillingIntervalName;
  seats: number;
  addonModuleCodes: string[];
}

export interface CreateSubscriptionRequest extends SubscriptionSelection {
  billingEmail: string;
}
